import { buildApiUrl, getApiBaseUrl } from "./apiBaseUrl";
import { clearAuthStorage, getStoredAuthData } from "./storage";

export { buildApiUrl, getApiBaseUrl };
export { clearAuthStorage, getStoredAuthData };

/**
 * Safely parse API payloads where `data` may be a JSON string or already an object.
 */
export function parseApiPayload(raw) {
  let parsed = raw?.data ?? raw;

  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch (error) {
      console.error("Failed to parse API response:", error);
      return null;
    }
  }

  return parsed ?? null;
}

const getVisitorId = () => {
  if (typeof window !== "undefined") {
    return localStorage.getItem("visitor_Id") || "";
  }
  return "";
};

const getAuthToken = () => {
  if (typeof window === "undefined") return null;
  const auth = getStoredAuthData();
  return auth && Object.keys(auth).length > 0 ? auth : null;
};

const refreshAuthToken = async () => {
  if (typeof window === "undefined") return null;
  const auth = getAuthToken();
  if (!auth?.refresh_token) return null;

  try {
    const loginUrl = buildApiUrl("Astrologer/Astrologer_Login");
    const refreshVal = {
      refresh_token: auth.refresh_token,
      grant_type: "refresh_token",
    };

    const response = await fetch(loginUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(refreshVal),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    const parseData = parseApiPayload(data);
    const Resdata = parseData?.Table;

    if (Array.isArray(Resdata) && Resdata.length > 0) {
      localStorage.setItem("LoginTokenData", JSON.stringify(Resdata[0]));
      return Resdata[0];
    }
    return null;
  } catch (error) {
    console.error("Token refresh error:", error);
    return null;
  }
};

const isAuthDenied = (response, responseData) =>
  !response.ok &&
  responseData?.Message === "Authorization has been denied for this request.";

export const fetchData = async (URL) => {
  try {
    const visitorId = getVisitorId();
    const response = await fetch(URL, {
      method: "GET",
      headers: {
        FingerPrintJsKey: visitorId,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return await response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    return null;
  }
};

export const getPostData = async (url, postDataBody) => {
  try {
    const visitorId = getVisitorId();
    const fullUrl = buildApiUrl(url);
    const response = await fetch(fullUrl, {
      method: "POST",
      headers: {
        FingerPrintJsKey: visitorId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(postDataBody),
    });

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const data = await response.json();
    const parseData = parseApiPayload(data);
    return parseData?.Table ?? null;
  } catch (error) {
    console.error("Post data error:", error);
    return null;
  }
};

export const postWithToken = async (url, postDataBody) => {
  const makeApiCall = async () => {
    const auth = getAuthToken();
    const visitorId = getVisitorId();
    const apiUrl = buildApiUrl(url);

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        FingerPrintJsKey: visitorId,
        Authorization: `Bearer ${auth?.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(postDataBody),
    });

    let responseData = null;
    try {
      responseData = await response.json();
    } catch (error) {
      console.error("Failed to read API JSON:", error);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return null;
    }

    if (isAuthDenied(response, responseData)) {
      const newAuth = await refreshAuthToken();
      if (newAuth) return await makeApiCall();
      return null;
    }

    if (!response.ok) {
      console.error(`HTTP ${response.status}: ${response.statusText}`);
      return null;
    }

    const finalData = parseApiPayload(responseData);
    return finalData?.Table || null;
  };

  try {
    return await makeApiCall();
  } catch (error) {
    console.error("postWithToken error:", error);
    return null;
  }
};

export const TokenWithDeleteUpadateAdd = async (url, postDataBody) => {
  const makeApiCall = async () => {
    const auth = getAuthToken();
    const visitorId = getVisitorId();
    const apiUrl = buildApiUrl(url);

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${auth?.access_token}`,
        "Content-Type": "application/json",
        FingerPrintJsKey: visitorId,
      },
      body: JSON.stringify(postDataBody),
    });

    let responseData = null;
    try {
      responseData = await response.json();
    } catch (error) {
      console.error("Failed to read API JSON:", error);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return null;
    }

    if (isAuthDenied(response, responseData)) {
      const newAuth = await refreshAuthToken();
      if (newAuth) return await makeApiCall();
      return null;
    }

    if (!response.ok) {
      console.error(`HTTP ${response.status}: ${response.statusText}`);
      return responseData || null;
    }

    if (typeof responseData === "string") {
      return parseApiPayload({ data: responseData }) || null;
    }

    return responseData || null;
  };

  try {
    return await makeApiCall();
  } catch (error) {
    console.error("TokenWithDeleteUpadateAdd error:", error);
    return null;
  }
};

export const TokenImageUpload = async (url, formData) => {
  const makeApiCall = async () => {
    const auth = getAuthToken();
    const visitorId = getVisitorId();
    const apiUrl = buildApiUrl(url);

    const response = await fetch(apiUrl, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${auth?.access_token}`,
        FingerPrintJsKey: visitorId,
      },
      body: formData,
    });

    let responseData = null;
    try {
      responseData = await response.json();
    } catch (error) {
      console.error("Failed to read API JSON:", error);
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      return null;
    }

    if (isAuthDenied(response, responseData)) {
      const newAuth = await refreshAuthToken();
      if (newAuth) return await makeApiCall();
      return null;
    }

    if (!response.ok) {
      console.error(`HTTP ${response.status}: ${response.statusText}`);
      return responseData || null;
    }

    if (typeof responseData === "string") {
      return parseApiPayload({ data: responseData }) || null;
    }

    return responseData || null;
  };

  try {
    return await makeApiCall();
  } catch (error) {
    console.error("TokenImageUpload error:", error);
    return null;
  }
};

export const postData = async (url, requestData) => {
  try {
    const visitorId = getVisitorId();
    const fullUrl = buildApiUrl(url);
    const response = await fetch(fullUrl, {
      method: "POST",
      headers: {
        FingerPrintJsKey: visitorId,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(requestData),
    });

    if (!response.ok) {
      console.error("HTTP error:", response.status, response.statusText);
      return null;
    }

    const data = await response.json();

    if (data?.data !== undefined && data.data !== null && data.data !== "") {
      let nested;
      try {
        nested = typeof data.data === "string" ? JSON.parse(data.data) : data.data;
      } catch (parseError) {
        console.error("JSON parsing error:", parseError);
        return data;
      }

      // Keep outer fields like `success` for SMS/OTP callers.
      if (typeof data === "object" && ("success" in data || "Message" in data)) {
        if (nested && typeof nested === "object" && !Array.isArray(nested)) {
          return { ...nested, ...data, data: nested };
        }
        return { ...data, data: nested };
      }

      return nested;
    }

    return data;
  } catch (error) {
    console.error("Post data error:", error);
    return null;
  }
};

export const saveAuthToken = (tokenData) => {
  if (typeof window !== "undefined") {
    localStorage.setItem("LoginTokenData", JSON.stringify(tokenData));
    const isUserAccount = tokenData?.Astro === "0" || tokenData?.Astro === 0;
    const astroId =
      !isUserAccount && tokenData?.Astro && String(tokenData.Astro) !== "0"
        ? tokenData.Astro
        : tokenData?.ID;
    if (astroId && String(astroId) !== "0" && !isUserAccount) {
      localStorage.setItem("AstroLoginId", String(astroId));
    }
    const userId = tokenData?.ID;
    if (userId && isUserAccount) {
      localStorage.setItem("UserLoginId", String(userId));
    }
  }
};

export const clearAuthData = () => {
  if (typeof window !== "undefined") {
    clearAuthStorage();
    try {
      sessionStorage.clear();
    } catch (error) {
      console.error("sessionStorage clear failed:", error);
    }
  }
};
